import { Formations } from "@/api/privateRequests/squad-builder/get-formations";
import { shouldShowAdsSelector } from "@/redux/user/userSlice";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import { APIStatus } from "../../../../../api/APIStatus.type";
import { addAlert } from "../../../../../redux/message/messageSlice";
import { updateTacticInsertStatus as sbUpdateTacticInsertStatus } from "../../../../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { AppDispatch } from "../../../../../redux/store";
import {
  Tactic,
  updateTacticInsertStatus as tacticsUpdateTacticInsertStatus,
} from "../../../../../redux/tactics/tacticsSlice";
import { Button } from "../../../../atoms/Buttons/Button";
import { InputField } from "../../../../molecules/InputField/InputField";
import { TacticInfo } from "../../../Tactics/TacticInfo";
import { TacticPlayerRoles } from "../../../Tactics/TacticPlayerRoles";
import { TacticalPreset } from "../../../Tactics/TacticalPreset";
import { PanelHeader } from "../../RightPanel/PanelHeader/PanelHeader";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";

type Props = {
  mode: "squad-builder" | "tactics-overview";
  onClose?: () => void;
  chosenTactic?: Tactic;
  handleUseTacticInSquadBuilder?: (tactic: Tactic) => void;
  handleCopy?: () => void;
  handleChangeFormation?: (formationId: string) => void;
  handleChangeBuildUpStyle?: (buildUpStyle: string) => void;
  handleChangeDefensiveApproach?: (defensiveApproach: string) => void;
  handleChangeLineHeight?: (lineHeight: number) => void;
  handleGetTacticFromCode?: (code: string) => void;
  statusInsertTacticCode?: APIStatus;
  desktopMode?: boolean;
  canUploadTactic?: boolean;
  handleUploadTacticsClicked?: () => void;
  formationLabel: string;
  formations: Formations;
};

type InsertCodeAndApplyProps = {
  eaCode: string;
  setEaCode: (code: string) => void;
  tacticErrorMessage: string;
  setTacticErrorMessage: (message: string) => void;
  handleGetTacticFromCode: (code: string) => void;
  updateTacticStatus: () => void;
};

export const InsertCodeAndApply: React.FC<InsertCodeAndApplyProps> = ({
  eaCode,
  setEaCode,
  tacticErrorMessage,
  setTacticErrorMessage,
  handleGetTacticFromCode,
  updateTacticStatus,
}) => {
  const onSubmit = () => {
    if (eaCode.length !== 11) {
      setTacticErrorMessage("Invalid EA code");
      return;
    }
    handleGetTacticFromCode(eaCode);
  };
  return (
    <>
      <InputField
        value={eaCode}
        onChange={(code) => {
          if (tacticErrorMessage) {
            setTacticErrorMessage("");
            updateTacticStatus();
          }
          setEaCode(code);
        }}
        onEnter={onSubmit}
        placeholder="Insert Tactics Code"
      />
      <Button onClick={onSubmit}>Apply</Button>
    </>
  );
};

export const TacticMode = ({
  mode,
  chosenTactic,
  onClose,
  handleCopy,
  handleChangeFormation,
  handleChangeBuildUpStyle,
  handleChangeDefensiveApproach,
  handleChangeLineHeight,
  handleGetTacticFromCode,
  statusInsertTacticCode,
  desktopMode,
  formationLabel,
  formations,
}: Props) => {
  const [eaCode, setEaCode] = useState("");
  const [tacticErrorMessage, setTacticErrorMessage] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const shouldShowAds = useSelector(shouldShowAdsSelector);

  useEffect(() => {
    if (statusInsertTacticCode === "rejected") {
      setTacticErrorMessage("Invalid EA Code");
    } else {
      setTacticErrorMessage("");
    }
    if (statusInsertTacticCode === "fulfilled") {
      dispatch(
        addAlert({
          title: "Tactic Applied",
          description: `Succesfully applied ${eaCode}`,
          type: "success",
        }),
      );
      if (!desktopMode) {
        window.scrollTo(0, 104);
      }
      if (mode === "squad-builder") {
        dispatch(sbUpdateTacticInsertStatus("idle"));
      } else {
        dispatch(tacticsUpdateTacticInsertStatus("idle"));
      }
      setEaCode("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusInsertTacticCode, dispatch]);

  const tacticTitle =
    chosenTactic?.name ||
    (mode === "squad-builder" ? "Custom Tactic" : "No Tactic Selected");

  const tacticDescription = chosenTactic?.description;
  const tacticImg = (
    <img
      alt={tacticTitle}
      src={
        chosenTactic?.logoUrl ||
        "http://cdn.easysbc.io/fc25/tactics/logos/preset2.png"
      }
      className="rounded-tl-lg  h-[92px] aspect-square"
    />
  );

  return (
    <div className="flex flex-col w-full">
      <PanelHeader
        title={"Tactic Template"}
        onClose={onClose}
        noCross={!chosenTactic}
      />
      <PanelContainer>
        <div
          className={clsx(
            "flex flex-col w-full mt-8 overflow-x-hidden overflow-y-auto gap-y-4",
            mode === "tactics-overview" && "pb-16",
            shouldShowAds && mode === "squad-builder" && "pb-64 sb-v2-lg:pb-0",
          )}
        >
          <div
            className={clsx(
              "flex flex-row justify-between gap-x-2 desktop:hidden",
            )}
          >
            <InsertCodeAndApply
              {...{
                eaCode,
                setEaCode,
                tacticErrorMessage,
                setTacticErrorMessage,
                handleGetTacticFromCode,
                updateTacticStatus: () =>
                  mode === "squad-builder"
                    ? dispatch(sbUpdateTacticInsertStatus("idle"))
                    : dispatch(tacticsUpdateTacticInsertStatus("idle")),
              }}
            />
          </div>
          {tacticErrorMessage && (
            <span className="flex pl-3 text-xs text-red-500 desktop:hidden">
              {tacticErrorMessage}
            </span>
          )}
          {chosenTactic && (
            <TacticInfo
              eaCode={chosenTactic?.eaCode}
              socials={chosenTactic?.socials || {}}
              name={tacticTitle}
              author={chosenTactic?.author}
              icon={tacticImg}
              tags={chosenTactic?.tags || []}
              description={tacticDescription}
              popularityScore={chosenTactic?.popularityScore || 0}
              handleCopy={handleCopy}
              noTactic={!chosenTactic}
              isSquadBuidler={mode === "squad-builder"}
            />
          )}
          {chosenTactic && (
            <TacticalPreset
              formationId={chosenTactic?.teamTactics?.formationId}
              formationLabel={formationLabel}
              buildUpStyle={chosenTactic?.teamTactics?.buildUpStyle}
              defensiveApproach={chosenTactic?.teamTactics?.defensiveApproach}
              lineHeight={chosenTactic?.teamTactics?.lineHeight}
              editable={mode === "squad-builder"}
              formations={formations}
              onChangeFormation={handleChangeFormation}
              onChangeBuildUpStyle={handleChangeBuildUpStyle}
              onChangeDefensiveApproach={handleChangeDefensiveApproach}
              onChangeLineHeight={handleChangeLineHeight}
            />
          )}
          {chosenTactic && (
            <TacticPlayerRoles
              roles={chosenTactic?.playerRoles}
              formationId={chosenTactic?.teamTactics?.formationId}
              isSquadBuilder={mode === "squad-builder"}
            />
          )}

          {(mode === "squad-builder" || !chosenTactic) && (
            <div className="flex flex-col mobile:px-2 sb-v2-lg:px-0 px-0 gap-y-4">
              {mode === "tactics-overview" && (
                <p className="text-sm text-gray-200">
                  Did you know that you can import any tactic using an EA
                  tactics code, and get our AI squad builder to build a squad
                  for you that fits the tactic perfectly?
                </p>
              )}
              <div className="flex-row hidden w-full gap-x-2 desktop:flex">
                <InsertCodeAndApply
                  {...{
                    eaCode,
                    setEaCode,
                    tacticErrorMessage,
                    setTacticErrorMessage,
                    handleGetTacticFromCode,
                    updateTacticStatus: () =>
                      mode === "squad-builder"
                        ? dispatch(sbUpdateTacticInsertStatus("idle"))
                        : dispatch(tacticsUpdateTacticInsertStatus("idle")),
                  }}
                />
              </div>
              {tacticErrorMessage && (
                <span className="hidden pl-3 text-xs desktop:flex text-red-500">
                  {tacticErrorMessage}
                </span>
              )}
            </div>
          )}
          <NavLink to={"/tactics"} className={"mx-auto pb-12"}>
            <Button onClick={null}>{"Browse Tactics ->"}</Button>
          </NavLink>
        </div>
      </PanelContainer>
    </div>
  );
};
